/**
 * /j/{ticketNumber} — the page a job label's QR opens.
 *
 * The label encodes the job, never its state, so the destination is decided
 * now: the API reads the job's live state (unfinished check-in, resolved,
 * closed, open) and this page redirects there. Mounted outside MainLayout,
 * since it is almost always opened from a phone camera.
 *
 * `replace` rather than a push, so Back does not bounce off the resolver.
 * Design: docs/qr-scan-workflow-requirements.md §9.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Container, Stack } from '@mui/material';

import { fetchScanTarget } from '../services/scanTargetApi';
import { scanTargetRoute } from '../utils/scanTargetRoute';

export const ScanJobPage: React.FC = () => {
  const { ticketNumber = '' } = useParams<{ ticketNumber: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!/^\d+$/.test(ticketNumber)) {
      setError(`"${ticketNumber}" is not a job number.`);
      return;
    }
    const controller = new AbortController();
    setError(null);

    fetchScanTarget(ticketNumber, controller.signal)
      .then(target => {
        if (controller.signal.aborted) return;
        if (!target) {
          setError(`No job found with number ${ticketNumber}.`);
          return;
        }
        navigate(scanTargetRoute(target), { replace: true });
      })
      .catch(err => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to look up job');
      });

    return () => controller.abort();
  }, [ticketNumber, navigate]);

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Stack spacing={2}>
          <Alert severity="error">{error}</Alert>
          <Stack direction="row" spacing={1}>
            {/^\d+$/.test(ticketNumber) && (
              <Button variant="contained" onClick={() => navigate(`/ticket/${ticketNumber}`)}>
                Open ticket {ticketNumber}
              </Button>
            )}
            <Button variant="outlined" onClick={() => navigate('/')}>
              Dashboard
            </Button>
          </Stack>
        </Stack>
      </Container>
    );
  }

  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50dvh' }}
    >
      <CircularProgress />
    </Box>
  );
};

export default ScanJobPage;
